// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core'
import * as React from 'react'
import Modal from 'react-bootstrap/lib/Modal'
import Glyphicon from 'react-bootstrap/lib/Glyphicon'
import { Mutation } from 'react-apollo'

import type { CommonActionProps } from './types.actions'
import ToggleController from '../../controllers/ToggleController'
import StatusController from '../../controllers/StatusController'
import ButtonAction from './ButtonAction'
import Prompt from '../Prompt'
import Loading from '../Loading'
import { transmitOrderMutation } from '../../graphql/mutations/orders/transmitOrder.mutation'
import getMenuItems from '../../graphql/queries/getMenuItems.queries'
import messages from '../../config/messages'
import { removeTypenamesFromOrderDet } from '../../utils'
import { HistoryContext } from '../../context/history.context'
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query';
import Base from "../../layout/Base.layout";
import { AnnouncementsContext } from '../../context/announcements.context';
import { DesktopView, MobileView } from '../../config/mediaQuery'

type Props = {
  Det: any,
  OrderDate?: string,
  OrderType?: string,
  onMutationCompleted: () => mixed,
}

type State = {
  transmiting: boolean,
}

class TransmitOrderAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
  }

  state = {
    transmiting: false,
  }

  transmitingTimerId: TimeoutID

  announcementsContext = this.context;

  onMutationCompleted = async ({ data, history }: { data: any, history: any }) => {
    if (data.transmitOrder && data.transmitOrder.error) {
      return
    }

    if (this.transmitingTimerId) {
      clearTimeout(this.transmitingTimerId)
    }

    this.setState({ transmiting: false });

    this.props.onMutationCompleted();

    this.announcementsContext.setMessage({
      key: 'anouncements',
      data: [
        {
          moduleName: 'transmitOrder',
          anouncements: ['success'],
        },
      ],
    });

    Base.getClient().query({ query: getActiveOrderQuery });

    history.push('/accounts');
  }

  componentWillUnmount = () => {
    if (this.transmitingTimerId) {
      clearTimeout(this.transmitingTimerId)
    }
  }

  onStartRemoving = ({ mutation, onToggleVisibility }: any) => async () => {
    this.setState({
      transmiting: true,
    })

    try {
      await mutation()

      if (typeof onToggleVisibility === 'function') {
        onToggleVisibility()
      }
    } catch (e) {
      // TODO: handle error
    }
  }

  onExit = (options: { onToggleVisibility: () => mixed, loading: boolean }) => {
    const { onToggleVisibility, loading } = options

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return

    onToggleVisibility()
  }

  onSubmit = () => { }

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => {
          return (
            <HistoryContext.Consumer>
              {(history) => {
                return (
                  <Mutation
                    mutation={transmitOrderMutation}
                    variables={{
                      input: {
                        OrderType: this.props.OrderType,
                        OrderDate: this.props.OrderDate,
                        Det: removeTypenamesFromOrderDet(this.props.Det),
                      },
                    }}
                    onCompleted={(data) =>
                      this.onMutationCompleted({ data, history })
                    }
                    refetchQueries={() => {
                      return [
                        {
                          query: getMenuItems,
                        },
                        {
                          query: getActiveOrderQuery
                        }
                      ];
                    }}
                    skip={!visible}
                  >
                    {(mutation, { loading, error }) => {
                      return (
                        <StatusController
                          hasError={!!error}
                          loading={loading}
                          delay={3000}
                        >
                          {({ status }) => {
                            return (
                              <React.Fragment>
                                <DesktopView>
                                  <ButtonAction
                                    bsSize={this.props.size}
                                    disabled={
                                      this.props.disabled ||
                                      loading ||
                                      (status !== '' &&
                                        status !== 'error' &&
                                        this.state.transmiting)
                                    }
                                    onClick={onToggleVisibility}
                                  >
                                    <Glyphicon glyph="arrow-right" />{' '}
                                    <span>
                                      {(status === '' || status === 'error') &&
                                        'Transmit Order'}
                                      {status === 'loading' &&
                                        'Transmiting order...'}
                                      {status === 'completed' &&
                                        'Order Transmited!'}
                                    </span>
                                  </ButtonAction>
                                </DesktopView>
                                <MobileView>
                                  <button 
                                    disabled={
                                      this.props.disabled ||
                                      loading ||
                                      (status !== '' &&
                                        status !== 'error' &&
                                        this.state.transmiting)
                                    }
                                    onClick={onToggleVisibility}
                                  >
                                    <img src='assets/transmit.svg' alt='transmit icon'/>                                       
                                    {(status === '' || status === 'error') &&
                                        'Transmit Order'}
                                      {status === 'loading' &&
                                        'Transmiting order...'}
                                      {status === 'completed' &&
                                        'Order Transmited!'}
                                  </button>
                                </MobileView>
                                {visible && (
                                  <MobileView>
                                    {(isMobile) => (
                                      <Modal
                                        show={visible}
                                        onHide={() =>
                                          this.onExit({
                                            onToggleVisibility,
                                            loading,
                                          })
                                        }
                                        className={'modal--form'}
                                        dialogClassName={isMobile ? 'confirmation-modal' : ''}
                                      >
                                        {!isMobile && 
                                          <Modal.Header closeButton>
                                            <Modal.Title>Transmit Order</Modal.Title>
                                          </Modal.Header>
                                        }
                                        <Modal.Body
                                          css={{
                                            minHeight: '300px',
                                          }}
                                        >
                                          {loading && <Loading />}
                                          <Prompt
                                            onCancel={() =>
                                              this.onExit({
                                                onToggleVisibility,
                                                loading,
                                              })
                                            }
                                            onConfirm={this.onStartRemoving({
                                              onToggleVisibility,
                                              mutation,
                                            })}
                                            hideActions={loading}
                                            message={
                                              loading
                                                ? messages.transmitOrder.loading
                                                : error
                                                  ? messages.transmitOrder.fail
                                                  : messages.transmitOrder.prompt
                                            }
                                            errorMessage={
                                              status === 'error'
                                                ? messages.transmitOrder.fail
                                                : ''
                                            }
                                          />
                                        </Modal.Body>
                                      </Modal>
                                    )}
                                  </MobileView>
                                )}
                              </React.Fragment>
                            )
                          }}
                        </StatusController>
                      )
                    }}
                  </Mutation>
                )
              }}
            </HistoryContext.Consumer>
          )
        }}
      </ToggleController>
    )
  }
}

TransmitOrderAction.contextType = AnnouncementsContext;

export default TransmitOrderAction
